import React, { FC, ReactNode, useMemo, useState } from 'react';
import { Header } from '../../features/silo/silo/Header';
import { AssetCards, PulseAssetCards } from '../../features/silo/silo/AssetCards';
import { ActionTabs } from 'app/features/silo/silo/ActionTabs';
import { useThisDecoratedSilo, useThisSiloAddress, useValidateSiloAddress } from 'app/features/silo/silo/hooks';
import { PositionsPanel } from 'app/features/silo/common/PositionsPanel';
import { Suspense } from 'app/features/silo/silo/Suspense';
import { PageProviders } from 'app/features/silo/PageProviders';
import { useSiloWeb3 } from '@silo/web3';
import { useIsSiloDeprecated } from 'app/features/silo/deprecated/useIsSiloDeprecated';
import { fromDecoratedSilo } from 'app/features/silo/common/PositionsPanel/utilities/fromFallbackPositions';
import { useSiloConfiguration } from '@silo/config-legacy';
import { PATH } from 'app/hooks/useRoute';
import { isUndefined } from '@silo/utils';
import { ActionPanels } from '../../features/silo/common/ActionForm/ActionForm';
import { PulseAPYChart, PulseUtilizationChart } from '../../features/silo/common/PulsePanel';
import { getIsLiquidationWarningEnabled } from '../../functions/feature-flags/feature-flags';
import { ErrorBoundary } from 'react-error-boundary';
import { useIsChartsFeatureOn } from '../../features/silo/common/ActionForm/useIsChartsFeatureOn';
import { useQueryReadyFallbackMarkets } from '../../queries/markets/fallbackMarkets';
import { P, match } from 'ts-pattern';
import { DEFAULT_PROTOCOL_KEY, makeAddressSync } from '@silo/types';
import {
  Banner,
  EmptyText,
  Link,
  PulseMarketHeader,
  PulsePositionsPanel,
  PulseTabs,
  Tabs,
  useIsMobile,
  Warning,
} from '@silo/ui';
import { LiquidationBanner } from 'app/features/nav/liquidation-banner';
import { getWarningConfig } from '@silo/config';

const Silo = () => {
  useValidateSiloAddress();

  const { status, protocolInstance, chainId } = useSiloWeb3();
  const thisSiloAddress = useThisSiloAddress();
  /**
   * Whenever the user goes from a deprecated silo straight to the latest silo,
   * we need to retrigger all the silo page fetches. This approach can be
   * cleaned up once the data fetching refactors are done.
   */
  const [key, setKey] = useState(0);
  const rekey = () => setKey((prev) => prev + 1);
  const isLiquidationWarningOn = getIsLiquidationWarningEnabled(chainId);

  return (
    <div className="flex flex-col gap-10">
      <Suspense fallback={<></>}>
        <div className="flex flex-col gap-2.5">
          <DeprecatedBanner rekey={rekey} />

          <WarningBanner />

          {thisSiloAddress && isLiquidationWarningOn ? <LiquidationBanner href={PATH.DASHBOARD} /> : null}
        </div>
      </Suspense>

      <SiloPage key={`${status}${key}${protocolInstance}`} />
    </div>
  );
};

const DeprecatedBanner: FC<{ rekey: () => void }> = ({ rekey }) => {
  const { siloAddress } = useThisDecoratedSilo();
  const result = useIsSiloDeprecated(siloAddress);

  if (!result.isDeprecated) return <></>;

  const { newSilo } = result;

  return (
    <Banner
      icon={<Banner.Icon className={Banner.icon({ className: 'text-amber-300' })} />}
      content={
        <p className="flex flex-col gap-1 w-full sm:flex-row">
          <span>
            <span className="font-medium">You are viewing a deprecated silo.</span>{' '}
            {newSilo ? (
              <span>
                Here is the{' '}
                <Link href={`/silo/${newSilo.id}`} onClick={rekey} className="!font-medium !text-white underline">
                  latest version
                </Link>{' '}
                of this silo.{' '}
              </span>
            ) : null}
          </span>
        </p>
      }
    />
  );
};

const WarningBanner = () => {
  const { siloAddress } = useThisDecoratedSilo();
  const { protocolInstance } = useSiloWeb3();

  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;
  const marketAddress = makeAddressSync(siloAddress);
  const warningConfig = getWarningConfig({ protocolKey, marketAddress });

  return match(warningConfig)
    .with({ scope: P.union('all', 'singleMarket') }, ({ warningKey }) => (
      <Warning.BannerWarning warningKey={warningKey} />
    ))
    .otherwise(() => null);
};

const SiloPage = () => {
  return (
    <>
      <React.StrictMode>
        <div className="flex flex-col gap-10">
          <ErrorBoundary
            fallback={
              <p>
                A system error has occured when displaying silo information. Please contact us for assistance if the
                error persists.
              </p>
            }
          >
            <Suspense fallback={<PulseMarketHeader />}>
              <Header />
            </Suspense>
          </ErrorBoundary>

          <SiloDisplay />
        </div>
      </React.StrictMode>
    </>
  );
};

const SiloDisplay: FC = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'openPositions' | 'siloInfo' | null>('openPositions');

  return (
    <>
      <div className="flex flex-col gap-6 md:flex-row">
        <div className="flex-[2] md:flex-grow-[3] lg:flex-grow-[2] space-y-6">
          <Suspense fallback={<TabsSkeletonLoader />}>
            <MainContent />
          </Suspense>
          {!isMobile && (
            <div>
              <Suspense fallback={<PulseAssetCards />}>
                <AssetCards />
              </Suspense>
            </div>
          )}
        </div>
        {!isMobile && (
          <div className="flex-[2]">
            <Suspense fallback={<PulsePositionsPanel />}>
              <SiloPositionsPanel />
            </Suspense>
          </div>
        )}

        {isMobile && (
          <div className="mt-6">
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              id="silo-mobile-tabs"
              variant="fixed"
              data={[
                {
                  value: 'openPositions',
                  label: 'Open positions',
                  content: (
                    <TabContentWrapper>
                      <Suspense fallback={<PulsePositionsPanel />}>
                        <SiloPositionsPanel />
                      </Suspense>
                    </TabContentWrapper>
                  ),
                },
                {
                  value: 'siloInfo',
                  label: 'Silo info',
                  content: (
                    <TabContentWrapper>
                      <Suspense fallback={<PulseAssetCards />}>
                        <AssetCards />
                      </Suspense>
                    </TabContentWrapper>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </>
  );
};

const MainContent: FC = () => {
  return <ActionTabs />;
};

const SiloPositionsPanel: FC = () => {
  const decoratedSilo = useThisDecoratedSilo();
  const { data: fallbackMarkets } = useQueryReadyFallbackMarkets();
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const positions = useMemo(
    () => fromDecoratedSilo({ getConfigurationForSilo, markets: fallbackMarkets, decoratedSilo, protocolInstance }),
    [fallbackMarkets, decoratedSilo, getConfigurationForSilo, protocolInstance]
  );

  if (isUndefined(protocolInstance)) return null;

  return (
    <ErrorBoundary fallback={<>{null}</>}>
      <PositionsPanel positions={positions} protocolInstance={protocolInstance} />
    </ErrorBoundary>
  );
};

interface TabContentWrapperProps {
  children?: ReactNode;
}

export function TabContentWrapper({ children }: TabContentWrapperProps): JSX.Element {
  return <div className="mt-4">{children}</div>;
}

export default Silo;
// Silo.whyDidYouRender = true;
Silo.Provider = PageProviders;

const TabsSkeletonLoader: FC = () => {
  const isChartsTabOn = useIsChartsFeatureOn();

  const panels = [
    <div className="flex flex-col gap-5">
      <div className="h-16 rounded-lg bg-shale-700" />
      <div className="w-1/4 rounded-lg bg-shale-700">
        <EmptyText />
      </div>
      <div className="w-1/2 rounded-lg bg-shale-700">
        <EmptyText />
      </div>
      <div className="h-12 rounded-lg bg-shale-700" />
    </div>,
    <div className="flex flex-col gap-5">
      {[...Array(3)].map((_, index) => (
        <div className="w-1/3 rounded-lg bg-shale-700" key={index}>
          <EmptyText />
        </div>
      ))}
    </div>,
    ...(isChartsTabOn ? [<PulseAPYChart />, <PulseUtilizationChart />] : []),
  ];

  return (
    <div className="animate-pulse">
      <div className="px-5 py-5 rounded-t-lg bg-shale-800">
        <PulseTabs tabs={['Deposit', 'Borrow']} />
      </div>
      <ActionPanels panels={panels} />
    </div>
  );
};
